import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  Gavel,
  Award,
  Users,
  ScrollText,
  Loader2,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";

export default function JudgeDashboard() {
  const [assignments, setAssignments] = useState([]);
  const [scores, setScores] = useState([]);
  const [rubric, setRubric] = useState(null);
  const [active, setActive] = useState(null);   // { assignment, delegate }
  const [scoreForm, setScoreForm] = useState({});
  const [comments, setComments] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/judge/assignments/me").then((r) => setAssignments(r.data || []));
    api.get("/judge/scores/me").then((r) => setScores(r.data || []));
    api.get("/judge/rubric").then((r) => setRubric(r.data));
  }, []);

  const startScoring = (assignment, delegate) => {
    setActive({ assignment, delegate });
    setScoreForm(rubric.rubric_keys.reduce((a, k) => ({ ...a, [k]: 70 }), {}));
    setComments("");
    setRecommendation("");
  };

  const submitScore = async () => {
    if (!active) return;
    setSubmitting(true);
    try {
      await api.post("/judge/scores", {
        delegate_id: active.delegate.id,
        event_id: active.assignment.event_id,
        committee_id: active.assignment.committee_id,
        rubric_scores: scoreForm,
        comments,
        award_recommendation: recommendation,
      });
      toast.success("Score submitted");
      const r = await api.get("/judge/scores/me");
      setScores(r.data || []);
      setActive(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  };

  const totalDelegates = assignments.reduce((a, x) => a + (x.delegates?.length || 0), 0);
  const totalScored = scores.length;
  const overallAvg = scores.length ? Math.round(scores.reduce((a, s) => a + (s.overall_score || 0), 0) / scores.length) : 0;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Judge / Evaluator</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Your <span className="font-serif italic text-[#0F4C81]">scoring desk</span>.
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Use a structured 10-point rubric. Your scores feed delegate scorecards and the
              global leaderboard, but final awards remain organiser-approved.
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-900 text-xs">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="font-semibold">Final awards: organiser-approved</span>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="judge-stats">
          <Stat icon={Users} label="Delegates assigned" value={totalDelegates} accent="text-[#0F4C81]" />
          <Stat icon={Award} label="Scores submitted" value={totalScored} accent="text-emerald-600" />
          <Stat icon={Gavel} label="Average score given" value={`${overallAvg}/100`} accent="text-[#D4AF37]" />
        </div>

        {/* Assignments */}
        <div className="mt-8 space-y-5" data-testid="judge-assignments">
          {assignments.length === 0 ? (
            <div className="p-10 rounded-xl bg-white border-dashed border-2 border-slate-200 text-center">
              <Gavel className="h-6 w-6 text-slate-400 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">No assignments yet — your secretariat will assign you to a committee soon.</p>
            </div>
          ) : assignments.map((a) => (
            <div key={a.id} className="rounded-2xl bg-white border border-slate-200 p-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Assignment</p>
                  <h3 className="mt-1 text-xl font-semibold tracking-tight text-[#0A192F]">
                    {a.event?.title}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Committee: {a.committee_id || "—"} · {a.delegates?.length || 0} delegates
                  </p>
                </div>
              </div>
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {a.delegates.map((d) => {
                  const scored = scores.find((s) => s.delegate_id === d.id);
                  return (
                    <div key={d.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-start justify-between gap-3" data-testid={`judge-delegate-${d.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-[#0A192F]">{d.name}</p>
                        <p className="text-xs text-slate-500">{d.email}</p>
                        {scored && (
                          <p className="mt-2 text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                            Scored · {scored.overall_score}/100
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => startScoring(a, d)}
                        className="bg-[#0F4C81] hover:bg-[#0A3860]"
                        data-testid={`score-delegate-${d.id}`}
                      >
                        {scored ? "Re-score" : "Score"} <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Scoring panel */}
        {active && rubric && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="judge-score-modal">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Score · 10-point rubric</p>
                  <h2 className="text-xl font-semibold tracking-tight text-[#0A192F]">{active.delegate.name}</h2>
                </div>
                <button onClick={() => setActive(null)} className="text-slate-500 hover:text-slate-900">×</button>
              </div>
              <div className="p-6 space-y-4">
                {rubric.rubric_keys.map((k) => (
                  <div key={k} data-testid={`rubric-row-${k}`}>
                    <div className="flex items-center justify-between text-sm">
                      <Label className="text-sm font-semibold">{rubric.labels[k]}</Label>
                      <span className="font-mono text-[#0F4C81]">{scoreForm[k] || 0}/100</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={scoreForm[k] || 0}
                      onChange={(e) => setScoreForm({ ...scoreForm, [k]: parseInt(e.target.value) })}
                      className="w-full mt-1 accent-[#0F4C81]"
                      data-testid={`rubric-${k}`}
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-xs">Comments / qualitative feedback</Label>
                  <Textarea rows={3} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="judge-comments" />
                </div>
                <div>
                  <Label className="text-xs">Award recommendation (advisory only)</Label>
                  <Input
                    value={recommendation}
                    onChange={(e) => setRecommendation(e.target.value)}
                    placeholder="e.g. Best Delegate, High Commendation"
                    data-testid="judge-recommendation"
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
                <Button
                  onClick={submitScore}
                  disabled={submitting}
                  className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
                  data-testid="judge-submit-score"
                >
                  {submitting ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Submitting…</> : "Submit score"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* History */}
        {scores.length > 0 && (
          <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6" data-testid="judge-history">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Evaluation history</p>
            <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#0A192F]">Your past scores</h2>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 border-b">
                  <tr>
                    <th className="py-2.5 font-semibold">Delegate</th>
                    <th className="py-2.5 font-semibold">Overall</th>
                    <th className="py-2.5 font-semibold">Recommendation</th>
                    <th className="py-2.5 font-semibold">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {scores.map((s) => (
                    <tr key={s.id}>
                      <td className="py-3 font-semibold text-[#0A192F]">{s.delegate_id}</td>
                      <td className="py-3"><span className="px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#0A192F] font-mono text-xs font-bold">{s.overall_score}/100</span></td>
                      <td className="py-3 text-slate-700 text-xs">{s.award_recommendation || "—"}</td>
                      <td className="py-3 text-slate-500 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <Icon className={`h-4 w-4 ${accent}`} />
      <p className={`mt-3 font-serif text-3xl ${accent}`}>{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</p>
    </div>
  );
}
